import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Check, Send, ChevronDown, AlertCircle, X, User } from 'lucide-react';
import httpClient from "../Api/axios";

export default function ReviewForm() {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.getElementById('status-dropdown');
      if (dropdown && !dropdown.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };
  
  const dropdownVariants = {
    hidden: { opacity: 0, y: -10, height: 0 },
    visible: { 
      opacity: 1, 
      y: 0, 
      height: 'auto',
      transition: {
        duration: 0.2
      }
    }
  };

  const notificationVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.2 } }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (rating === 0 || reviewText.trim() === '' || name.trim() === '') {
      showNotification('Please provide a name, rating, and review text', 'error');
      return;
    }
  
    const payload = {
      star_count: rating,
      status: status || "Good",
      name: name,
      review: reviewText
    };
  
    setIsLoading(true);
  
    try {
      const response = await httpClient.post('/reviews', payload);
      console.log('Response:', response.data);
  
      setStatus(status || "Good");
      setFormSubmitted(true);
      showNotification('Your review has been submitted successfully!', 'success');
  
      setTimeout(() => {
        setStatus('approved');
        showNotification('Your review has been approved!', 'success');
      }, 3000);
  
    } catch (error) {
      console.error('Error submitting review:', error);
      showNotification('Failed to submit review. Please try again later.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const showNotification = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setShowMessage(true);
    
    setTimeout(() => {
      setShowMessage(false);
    }, 4000);
  };

  const isSubmitDisabled = rating === 0 || reviewText.trim() === '' || name.trim() === '' || isLoading;

  return (
    <div className="p-6 min-h-[600px] flex items-center justify-center bg-gray-100">

      {/* Notification */}
      <AnimatePresence>
        {showMessage && (
          <motion.div 
            className="fixed top-4 right-4 px-4 py-3 rounded-lg shadow-md bg-white border flex items-center space-x-2 max-w-sm z-50"
            variants={notificationVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="flex-1">{message}</div>
            <button 
              onClick={() => setShowMessage(false)}
              className="p-1 rounded-full hover:bg-gray-100"
            >
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {formSubmitted ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md mx-auto bg-blue-50 rounded-xl shadow-lg p-8 border border-blue-100 text-center"
        >
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center bg-blue-100">
              {status === 'approved' ? (
                <Check size={32} className="text-black-600" />
              ) : (
                <motion.div 
                  className="text-2xl animate-pulse text-black-500"
                  animate={{ opacity: [0.6, 1, 0.6] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >
                  ⏳
                </motion.div>
              )}
            </div>
            
            <h2 className="text-2xl font-semibold text-black-800">
              {status === 'approved' ? 'Review Approved!' : 'Review Submitted!'}
            </h2>
            
            <p className="text-black-700 max-w-sm">
              {status === 'approved' 
                ? 'Thank you for your feedback! Your review has been approved and is now visible to other users.'
                : 'Thank you for your feedback! Your review is currently being evaluated by our team.'}
            </p>
            
            <div className="mt-2 bg-blue-100 rounded-lg border border-blue-200 w-full p-3">
              <p className="text-black-800 font-medium">{name}</p>
            </div>
            
            <div className="flex items-center">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  size={24}
                  className={`${
                    rating >= star
                      ? 'text-amber-500 fill-amber-500'
                      : 'text-black-200'
                  }`}
                />
              ))}
              <span className="ml-2 text-black-700 font-medium">{rating}/5</span>
            </div>
          </div>
        </motion.div>
      ) : (
        <motion.div 
          className="w-full max-w-md mx-auto bg-blue-50 rounded-xl shadow-lg p-6 border border-blue-100"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <form onSubmit={handleSubmit} className="space-y-5">
          <motion.div
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="text-2xl font-bold text-center text-black-800 mb-6"
            >
             Unity Ortho and Eye Hospital            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="text-2xl font-bold text-center text-black-800 mb-6"
            >
             Write Review 
            </motion.div>
            
            {/* Header with Status Dropdown */}
            {/* <motion.div 
              className="flex items-center justify-between"
              variants={itemVariants}
               >
              <div className="flex items-center">
                <h2 className="text-xl font-semibold text-black-700">Your Review</h2>
              </div>
              
              <div className="relative" id="status-dropdown">
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-100 flex items-center space-x-2 transition-all duration-200 text-sm shadow-sm hover:shadow text-blue-700"
                >
                  <span>{status || 'Select Status'}</span>
                  <ChevronDown size={14} className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {isDropdownOpen && (
                    <motion.div 
                      className="absolute right-0 mt-1 w-36 bg-white rounded-lg shadow-md border border-blue-100 z-10 overflow-hidden"
                      variants={dropdownVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                    >
                      <ul>
                        {[
                          { value: 'Good', label: 'Good' },
                          { value: 'Better', label: 'Better' },
                          { value: 'Very Good', label: 'Very Good' },
                          { value: 'Excellent', label: 'Excellent' }
                        ].map((statusOption) => (
                          <li key={statusOption.value}>
                            <button
                              type="button"
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${status === statusOption.value ? 'font-medium bg-blue-50' : ''} text-blue-700 first:rounded-t-lg last:rounded-b-lg flex items-center justify-between`}
                              onClick={() => {
                                setStatus(statusOption.value);
                                setIsDropdownOpen(false);
                              }}
                            >
                              <span>{statusOption.label}</span>
                              {status === statusOption.value && <Check size={14} className="text-black-600" />}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
             */}
            {/* Decorative Divider */}
            <motion.div 
              variants={itemVariants} 
              className="h-px bg-blue-200"
            />
            
            {/* Name Field */}
            <motion.div 
      className="bg-blue-100 p-3 sm:p-5 rounded-lg shadow-sm border border-blue-200 w-full max-w-full"
      variants={itemVariants}
    >
      <div className="mb-2">
        <label htmlFor="name" className="text-sm sm:text-base font-medium">Your Name:</label>
      </div>
      
      <div className="relative">
        <div className="flex w-full">
          <span className="inline-flex items-center px-2 sm:px-3 py-2 rounded-l-md border border-r-0 border-blue-200 bg-blue-50 text-blue-500">
            <User size={16} className="sm:w-5 sm:h-5" />
          </span>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your name"
            className="flex-1 p-1 sm:p-2 text-sm sm:text-base border rounded-r-lg ring-0 focus:ring-2 transition-all duration-200 bg-white border-blue-200 focus:border-blue-300 focus:ring-blue-200"
            maxLength={50}
          />
        </div>
      </div>
    </motion.div>
            
            {/* Star Rating */}
            <motion.div 
              className="bg-blue-100 p-5 rounded-lg shadow-sm border border-blue-200"
              variants={itemVariants}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-black-700 font-medium">How would you rate your experience?</p>
                {rating > 0 && (
                  <span className="text-sm font-medium px-2 py-0.5 rounded bg-blue-200 text-black-700">
                    {rating === 5 ? 'Excellent' : 
                     rating === 4 ? 'Very Good' : 
                     rating === 3 ? 'Good' : 
                     rating === 2 ? 'Fair' : 'Poor'}
                  </span>
                )}
              </div>
              <div className="flex items-center">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <motion.button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(0)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className="focus:outline-none mr-1"
                    >
                      <Star
                        size={28}
                        className={`${
                          (hoveredRating || rating) >= star
                            ? 'text-amber-500 fill-amber-500'
                            : 'text-black-200'
                        } transition-colors duration-200`}
                      />
                    </motion.button>
                  ))}
                </div>
                <span className="ml-3 text-black-600 font-medium">{rating > 0 ? `${rating}/5` : ''}</span>
              </div>
            </motion.div>
            
            {/* Review Text */}
            <motion.div 
              className="bg-blue-100 p-5 rounded-lg shadow-sm border border-blue-200"
              variants={itemVariants}
            >
              <div className="mb-2">
                <label htmlFor="review" className="text-black-700 font-medium">Share your experience (optional):</label>
              </div>
              
              <div className="relative">
                <textarea
                  id="review"
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder="Tell us about your experience..."
                  className="w-full p-4 border rounded-lg ring-0 focus:ring-2 transition-all duration-200 min-h-32 resize-none bg-white text-blue-700 border-blue-200 focus:border-blue-300 focus:ring-blue-200"
                  maxLength={500}
                />
              </div>
              
              <div className="flex justify-between mt-2">
                <span className="text-xs text-black-500">Be honest and specific about your experience</span>
                <span className="text-xs text-black-500">
                  {reviewText.length}/500 characters
                </span>
              </div>
            </motion.div>
            
            {/* Submit Button */}
            <motion.div 
              variants={itemVariants}
              className="flex justify-end"
            >
              <motion.button
                type="submit"
                disabled={isSubmitDisabled}
                className={`flex items-center px-5 py-2.5 rounded-lg font-medium shadow-sm ${
                  isSubmitDisabled 
                    ? 'bg-blue-300 text-black-100 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                } transition-all duration-200`}
                whileHover={!isSubmitDisabled ? { scale: 1.03, boxShadow: "0 4px 8px -2px rgba(0, 0, 0, 0.1)" } : {}}
                whileTap={!isSubmitDisabled ? { scale: 0.97 } : {}}
              >
                <Send size={16} className="mr-2" />
                Submit Review
              </motion.button>
            </motion.div>
          </form>
        </motion.div>
      )}
    </div>
  );
}